// Forked from api/msw/api/cases/mockData.ts at `cases-pre-sr-fork` (Service Requests start as a copy of Cases).
// Comments below were written for Cases and still describe this file until the two diverge.
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getCompanyContactListData,
  _getCCTDataById,
} from '../companyContacts/mockData'
import { getServiceRequestNoteListDataByServiceRequestId } from '../serviceRequestNotes/mockData'
import { getServiceRequestDocumentListDataByServiceRequestId } from '../serviceRequestDocuments/mockData'
import { getServiceRequestEmailListDataByServiceRequestId } from '../serviceRequestEmails/mockData'
import { getCompanyMSServiceDataById } from '../companyMSServices/mockData'
import { ServiceRequestSource } from '@/types/serviceRequest/models'
import type {
  IServiceRequest,
  IServiceRequestOverview,
} from '@/types/serviceRequest/models'
import type { ICompanyMSService } from '~/types/company/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/api/servicerequests/mockData.ts]'

const serviceRequestListData = undefined as unknown as IServiceRequest[]

/**
 * Replace a case's stored `{id, label}` with the WHOLE companyMSService.
 *
 * `IServiceRequest.companyMSService` is an `ICompanyMSService`, not an
 * `IEntitySelectItem`: the Service Requests card renders columns off the service itself
 * -- `companyMSService.keyIdentifierValue`, the value that tells five
 * identical POTS lines apart -- and a select item carries only a label.
 *
 * WHY RESOLVED ON READ RATHER THAN STORED WHOLE. The seed keeps the id and
 * label a user's pick actually sends, and this re-reads the live row on every
 * list read, so a case can never show a stale copy of a service that has since
 * been edited. It also means the seed does not carry four near-duplicate
 * msServices. Same idiom as companyMasterServices' own forOfFunction, which
 * attaches companyMSServices the same way.
 *
 * ⚠ Going through `getCompanyMSServiceDataById` is deliberate: that function
 * sets `keyIdentifierValue` from the product's fieldSet
 * (api/msw/modules/setKeyIdentifierValues.ts), so the resolved service arrives
 * with the key identifier already on it.
 *
 * ⚠ A case may point at an id that no longer exists -- the msService was
 * deleted, or mock data was reset while cases were persisted. That must not
 * take out the whole Service Requests list, so the row keeps whatever it already had.
 */
async function _resolveCompanyMSService(theServiceRequest: IServiceRequest) {
  const fn = `${mn} _resolveCompanyMSService |`
  const companyMSServiceId = theServiceRequest.companyMSService?.id
  if (!companyMSServiceId) {
    return
  }
  try {
    theServiceRequest.companyMSService = await getCompanyMSServiceDataById(
      companyMSServiceId
    )
  } catch (e) {
    console.debug(
      `${fn} service request ${theServiceRequest.hrid} points at companyMSService` +
        ` '${companyMSServiceId}', which does not exist. Leaving as is.`,
      e
    )
  }
}

export const getServiceRequestListData = getListDataFunctionFactory<
  IServiceRequest[]
>({
  listData: serviceRequestListData,
  storageKey: 'servicerequests',
  getStartingListData: getStartingServiceRequestListData,
  forOfFunction: async (theServiceRequest) => {
    await _resolveCompanyMSService(theServiceRequest as IServiceRequest)
  },
})

/**
 * A case's companyMSService AS STORED: the `{id, label}` a user's pick sends.
 *
 * ⚠ The cast is the honest description of a real asymmetry, not a shortcut.
 * `IServiceRequest.companyMSService` types what a READ returns -- the whole service,
 * assembled by `_resolveCompanyMSService` above -- while a WRITE puts only the
 * select item there, because that is all a `doReturnObject` select emits
 * (services/entityInputFieldsService/modules/serviceRequest.ts). The seed is the stored
 * half, so it is shaped like a write, and every read resolves it.
 */
function storedCompanyMSService(
  id: TEntityId,
  label: string
): ICompanyMSService {
  return { id, label } as unknown as ICompanyMSService
}

/**
 * ⚠ EACH CASE CARRIES EXACTLY ONE ANCHOR -- a companyMSService OR a supplier,
 * never both. That is what the Anchor Entity control in the Service Request input spec
 * means: a case is raised either against one active service or against a
 * supplier with no specific service, and the anchor is recorded by WHICH of the
 * two FKs is set (`servicerequests` has both columns; a row fills one).
 *
 * They all carried both until 2026-09-21, which is a state the model calls
 * contradictory -- and it hid a real question, because no seeded case exercised
 * the supplier-anchored edit path at all. SR0001/SR0002 are service-anchored,
 * SR0003/SR0004 supplier-anchored, so both halves of the form have data.
 *
 * ⚠ The unused anchor is spelled `null`, not omitted. Every IServiceRequest property is
 * required-and-nullable, which is the same contract the server enforces on a
 * create: present, with null where there is no value.
 */
export async function getStartingServiceRequestListData(): Promise<
  IServiceRequest[]
> {
  const cctList = await getCompanyContactListData()
  return [
    {
      id: 'XXSR0001',
      hrid: 'SR0001',
      descr: 'POTS line at Boston dropping calls',
      openedAt: '2022-03-01T14:05:12.418302Z',
      createdAt: '2022-03-01T14:05:12.418302Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0096', 'POTS - Boston'),
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 6, label: 'Escalated' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSR0002',
      hrid: 'SR0002',
      descr: 'Static on the Boston fax line',
      openedAt: '2022-03-10T15:32:47.093114Z',
      createdAt: '2022-03-10T15:32:47.093114Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0097', 'POTS - Boston'),
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 5, label: 'Active (Other)' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXSR0003',
      hrid: 'SR0003',
      descr: 'No dial tone after overnight power cut',
      openedAt: '2022-03-06T16:48:03.771950Z',
      createdAt: '2022-03-06T16:48:03.771950Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: null,
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 2, label: 'Pending Cust Info' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSR0004',
      hrid: 'SR0004',
      descr: 'Intermittent outbound call failures',
      openedAt: '2022-03-02T13:21:39.250006Z',
      createdAt: '2022-03-02T13:21:39.250006Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: null,
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 3, label: 'Pending Dispatch' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: false,
    },
    {
      // ⚠ THE OTHER ANCHOR, SETTLED. Neither a supplier nor a service, and a
      // status that is NOT `From Email / Needs Review` -- so `isAwaitingTriage`
      // is false and anchorEntity's runOnOpen takes its `OTHER` fallback.
      //
      // That combination had no fixture, and it is the branch the triage work
      // deliberately did not touch: SR0005/SR0006 also carry neither FK, but
      // they are awaiting review, so they take the new "leave it unanswered"
      // path instead. Without this row the fallback -- and the
      // `# Loc. Affected` suppression it exists to produce -- is unexercised.
      //
      // Carries the SECOND case type, which moved here when SR0006's went null
      // to match what case_create_from_email.py actually creates.
      //
      // `numLocationsAffected` is null because the Other anchor unrenders that
      // field: a question about a supplier's or a service's locations has
      // nothing to be about when the case names neither.
      //
      // Status `Active (In Progress)` is the WIDEST case-status label, and the
      // one the Status column on ServiceRequestsCard is sized from. Nothing else rendered
      // it, so the column's binding constraint went unseen on screen.
      id: 'XXSR0007',
      hrid: 'SR0007',
      descr: 'Update the billing contact on our account',
      openedAt: '2022-03-16T17:09:54.602873Z',
      createdAt: '2022-03-16T17:09:54.602873Z',
      type: { id: 'XXSRT0002', label: 'Address Change' },
      numLocationsAffected: null,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: null,
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 4, label: 'Active (In Progress)' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    // ─────────────────────────────────────────────────────────────────────
    // THE THREE STATUSES NOTHING ELSE REACHES: Resolved, Canceled, Paused.
    //
    // Seeded so the Show-For-Status menu is demonstrable end to end -- before
    // these, three of its eleven options returned an empty list, and 'All Open'
    // was indistinguishable from 'All' because no case was ever closed. A
    // filter that cannot be seen excluding anything is a filter nobody can
    // check.
    //
    // ⚠ Resolved and Canceled are the whole of TERMINAL_CASE_STATUS_IDS, so
    // these two are what 'All Open' must hide. Paused is the third on purpose:
    // it is the judgement call in that list -- work stopped, the case did not
    // end -- so it must STAY in 'All Open', and only a seeded row shows that.
    // ─────────────────────────────────────────────────────────────────────
    {
      // Closed out: the service was restored and the case finished normally.
      // Service-anchored, like the case it mirrors (SR0001), so a terminal case
      // is not also an odd shape in every other respect.
      id: 'XXSR0008',
      hrid: 'SR0008',
      descr: 'Dropped calls on the Boston POTS line',
      openedAt: '2022-02-02T14:56:21.938417Z',
      createdAt: '2022-02-02T14:56:21.938417Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0096', 'POTS - Boston'),
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 9, label: 'Resolved' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    {
      // Withdrawn by the customer before anyone was dispatched. Supplier-
      // anchored, so the second terminal status is not a clone of the first.
      id: 'XXSR0009',
      hrid: 'SR0009',
      descr: 'Requested a survey, then withdrew the request',
      openedAt: '2022-02-11T15:13:08.144529Z',
      createdAt: '2022-02-11T15:13:08.144529Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: null,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: null,
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      assignedTo: null,
      status: { id: 8, label: 'Canceled' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    {
      // ⚠ PAUSED IS OPEN. Waiting on hardware, not finished -- so this row must
      // appear under 'All Open' alongside the active ones. It is the only case
      // that demonstrates isOpenServiceRequestStatus's one judgement call on screen.
      id: 'XXSR0010',
      hrid: 'SR0010',
      descr: 'Replacement handset on back-order from the supplier',
      openedAt: '2022-03-18T16:27:45.507391Z',
      createdAt: '2022-03-18T16:27:45.507391Z',
      type: { id: 'XXSRT0004', label: 'Change' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0097', 'POTS - Boston'),
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 7, label: 'Paused' },
      source: ServiceRequestSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    // ─────────────────────────────────────────────────────────────────────
    // Created by EMAIL-TO-CASE, and not yet triaged by anyone.
    //
    // ⚠ The pathway knows almost nothing. An inbound message arrives at the
    // support address, a case is opened from it and its body is stored as a
    // note authored by Service Request Bot -- but nothing in that flow can say which
    // service or supplier the customer means, so both anchors are null (the
    // Other anchor) and `assignedTo` is null until a human picks it up. That
    // is a state no amount of clicking in the app produces, which is why it
    // has to be seeded: the four cases above were all authored as though a
    // person filled the dialog in.
    //
    // Both carry status 1, `From Email / Needs Review`. That status exists to
    // cue a human to pick an Anchor Entity and fill in the service or supplier
    // the message is about. ⚠ It is only valid while BOTH anchors are null --
    // once a triager sets companyMSService or supplier the case moves on to a
    // worked status, so the four anchored cases above must never carry it.
    //
    // `ServiceRequestSource.EMAIL` is the DURABLE marker of the pathway (issue #1635) and
    // is what tells the two apart from a manually-raised case forever. The
    // status does not: it stops being `From Email / Needs Review` the moment
    // someone triages. Two markers, two different questions.
    //
    // `descr` is the SUBJECT LINE, because that is the only name the pathway
    // has for the case.
    // ─────────────────────────────────────────────────────────────────────
    {
      // Sender RESOLVED to a known contact -- Bilbo Baggins at Boston Coach,
      // matched on his address.
      id: 'XXSR0005',
      hrid: 'SR0005',
      descr: 'Static on every call from the Sunset office',
      openedAt: '2022-03-12T13:44:30.861208Z',
      createdAt: '2022-03-12T13:44:30.861208Z',
      type: null,
      numLocationsAffected: null,
      contact: _getCCTDataById('XXCCT0031', cctList) ?? null,
      companyMSService: null,
      supplier: null,
      assignedTo: null,
      status: { id: 1, label: 'From Email\nNeeds Review' },
      source: ServiceRequestSource.EMAIL,
      version: '1',
      canBeDeleted: true,
    },
    {
      // Sender NOT known -- nobody in the system has this address, so the
      // pathway leaves `contact` null rather than guessing. ⚠ The one seeded
      // case with no contact at all, and therefore the only one that exercises
      // ServiceRequest()'s null-contact guard on the Service Requests list, where the factory runs
      // per row inside a bare forEach with no try/catch.
      //
      // ⚠ `type` is null, as are BOTH anchors and `assignedTo`: that is exactly
      // what case_create_from_email.py creates. It validates a Service Request with only
      // id / descr / openedAt / status and lets every other column default,
      // so a case cannot arrive from email already knowing its type. The second
      // case TYPE moved to SR0007 to keep that coverage.
      id: 'XXSR0006',
      hrid: 'SR0006',
      descr: 'Please update the billing address on our account',
      openedAt: '2022-03-14T18:02:16.329740Z',
      createdAt: '2022-03-14T18:02:16.329740Z',
      type: null,
      numLocationsAffected: null,
      contact: null,
      companyMSService: null,
      supplier: null,
      assignedTo: null,
      status: { id: 1, label: 'From Email\nNeeds Review' },
      source: ServiceRequestSource.EMAIL,
      version: '1',
      canBeDeleted: true,
    },
  ]
}

export async function getServiceRequestDataById(
  id: TEntityId
): Promise<IServiceRequest | undefined> {
  const list = await getServiceRequestListData()
  return list?.find((serviceRequestData) => serviceRequestData.id === id)
}

/**
 * The case overview payload, mirroring getSupplierOverviewData.
 *
 * ⚠ NO `lastSelectedState` key, and that is deliberate -- unlike every other
 * overview payload in this app. There is no /api/servicerequests/{id}/lastselectedstate
 * resource, ServiceRequestOverview declares no such field, and the Service Request overview is a
 * single tabless page with no tab selection to persist. Do not "fix" this by
 * adding one: see the note in api/msw/api/suppliers/mockData.ts for what the
 * ABSENCE of the key costs on a silo that genuinely needs it, and the page's own
 * comment for why Service Requests does not.
 *
 * ⚠ `serviceRequestEmails` includes rows whose `body` is null (auto-replies,
 * bounces, spam), as the case overview's `caseEmails` does: the Emails card
 * must filter them rather than the payload hiding them.
 */
export async function getServiceRequestOverviewData(
  id: TEntityId
): Promise<IServiceRequestOverview | undefined> {
  const serviceRequestData = await getServiceRequestDataById(id)
  if (!serviceRequestData) {
    return undefined
  }
  return {
    // ⚠ NO standalone `contact` key. There used to be one, because the server
    // built the two from different views and only the overview's was whole.
    // `serviceRequest.contact` is a whole TContact now, so this payload carries exactly
    // one contact -- and the mock must NOT put the same object at two keys
    // again: that shared reference is what made the old split invisible
    // offline while the real API returned two different objects.
    serviceRequest: serviceRequestData,
    serviceRequestNotes:
      (await getServiceRequestNoteListDataByServiceRequestId(id)) || [],
    serviceRequestDocuments:
      (await getServiceRequestDocumentListDataByServiceRequestId(id)) || [],
    serviceRequestEmails:
      (await getServiceRequestEmailListDataByServiceRequestId(id)) || [],
  }
}
